import { Enum } from '../../../common/constants'
import { InvoiceSummaryWithCustomerJobData, wasInvoiceSent } from '../../../domain/invoicing/invoice'

export const BulkSelectionModes = {
  Specific: 'specific',
  All: 'all',
} as const

export type BulkSelectionMode = Enum<typeof BulkSelectionModes>

export type SelectedInvoiceMeta = {
  total: number
  wasEmailSent: boolean
  isExported: boolean
}

export type SpecificSelection = {
  mode: typeof BulkSelectionModes.Specific
  picked: Map<string, SelectedInvoiceMeta>
}

export type AllSelection = {
  mode: typeof BulkSelectionModes.All
  allIds: string[]
  excluded: Map<string, SelectedInvoiceMeta>
}

export type BulkSelection = SpecificSelection | AllSelection

export const emptySelection = (): BulkSelection => ({ mode: BulkSelectionModes.Specific, picked: new Map() })

const metaFromSummary = (summary: InvoiceSummaryWithCustomerJobData): SelectedInvoiceMeta => ({
  total: summary.total,
  wasEmailSent: wasInvoiceSent(summary),
  isExported: summary.qboInvoiceId.length > 0,
})

export const isRowSelected = (selection: BulkSelection, invoiceId: string): boolean => {
  if (selection.mode === BulkSelectionModes.Specific) {
    return selection.picked.has(invoiceId)
  }
  return !selection.excluded.has(invoiceId)
}

export const toggleRow = (selection: BulkSelection, summary: InvoiceSummaryWithCustomerJobData): BulkSelection => {
  if (selection.mode === BulkSelectionModes.Specific) {
    const picked = new Map(selection.picked)
    if (picked.has(summary.invoiceId)) {
      picked.delete(summary.invoiceId)
    } else {
      picked.set(summary.invoiceId, metaFromSummary(summary))
    }
    return { mode: BulkSelectionModes.Specific, picked }
  }
  const excluded = new Map(selection.excluded)
  if (excluded.has(summary.invoiceId)) {
    excluded.delete(summary.invoiceId)
  } else {
    excluded.set(summary.invoiceId, metaFromSummary(summary))
  }
  return { mode: BulkSelectionModes.All, allIds: selection.allIds, excluded }
}

export const selectAll = (allIds: string[]): BulkSelection => ({
  mode: BulkSelectionModes.All,
  allIds,
  excluded: new Map(),
})

export const selectedCount = (selection: BulkSelection): number => {
  if (selection.mode === BulkSelectionModes.Specific) {
    return selection.picked.size
  }
  return selection.allIds.length - selection.excluded.size
}

export const selectedInvoiceIds = (selection: BulkSelection): string[] => {
  if (selection.mode === BulkSelectionModes.Specific) {
    return Array.from(selection.picked.keys())
  }
  return selection.allIds.filter(id => !selection.excluded.has(id))
}

export const selectedTotalCents = (selection: BulkSelection, totalInvoicedCents: number): number => {
  if (selection.mode === BulkSelectionModes.Specific) {
    let sum = 0
    selection.picked.forEach(meta => { sum += meta.total })
    return sum
  }
  let excludedSum = 0
  selection.excluded.forEach(meta => { excludedSum += meta.total })
  return totalInvoicedCents - excludedSum
}

// In 'specific' mode this is exact — we cached `isExported` on every picked row.
// In 'all' mode we don't have per-row data for unloaded pages, so we fall back to
// scanning currently-loaded summaries. If an exported row exists on a page the user
// hasn't visited, this will return false. Acceptable for a confirmation-dialog hint;
// the user can still tick the checkbox manually.
export const isAnyExportedInSelection = (selection: BulkSelection, loadedSummaries: InvoiceSummaryWithCustomerJobData[]): boolean => {
  if (selection.mode === BulkSelectionModes.Specific) {
    for (const meta of selection.picked.values()) {
      if (meta.isExported) {
        return true
      }
    }
    return false
  }
  return loadedSummaries.some(x => !selection.excluded.has(x.invoiceId) && x.qboInvoiceId.length > 0)
}

export const selectedAlreadySentCount = (selection: BulkSelection, sentCount: number): number => {
  if (selection.mode === BulkSelectionModes.Specific) {
    let count = 0
    selection.picked.forEach(meta => {
      if (meta.wasEmailSent) {
        count += 1
      }
    })
    return count
  }
  let excludedSent = 0
  selection.excluded.forEach(meta => {
    if (meta.wasEmailSent) {
      excludedSent += 1
    }
  })
  return sentCount - excludedSent
}
